"use client";

import { c as _c } from "react/compiler-runtime";
import { FallbackImage } from "@/components/common/FallbackImage";
import { fullScreenSupported } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useInView } from "@/hooks/useInView";
import { faExpand, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ArrowTopRightOnSquareIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import Link from "next/link";
import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import useKeyPressEvent from "react-use/lib/useKeyPressEvent";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
const tooltipProps = {
  delayShow: 250,
  place: "top" as const,
  style: {
    backgroundColor: "#37373E",
    color: "white",
    zIndex: 1002
  }
};
const modalButtonClasses = "tw-flex tw-items-center tw-justify-center tw-border-0 tw-text-iron-50 tw-bg-iron-800 desktop-hover:hover:tw-bg-iron-700 tw-rounded-full tw-size-10 tw-flex-shrink-0 tw-backdrop-blur-sm tw-transition-all tw-duration-300 tw-ease-out";
function DropListItemContentMediaImage(t0) {
  const $ = _c(62);
  const {
    src,
    maxRetries: t1,
    onContainerClick,
    isCompetitionDrop: t2,
    disableModal: t3,
    imageObjectPosition,
    imageScale: t4
  } = t0;
  const maxRetries = t1 === undefined ? 0 : t1;
  const isCompetitionDrop = t2 === undefined ? false : t2;
  const disableModal = t3 === undefined ? false : t3;
  const imageScale = t4 === undefined ? ImageScale.AUTOx450 : t4;
  const [ref, inView] = useInView();
  const [loaded, setLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [errorCount, setErrorCount] = useState(0);
  const [retryTick, setRetryTick] = useState(0);
  const {
    hasTouchScreen
  } = useDeviceInfo();
  const imgRef = useRef(null);
  const modalImageRef = useRef(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const {
    isCapacitor
  } = useCapacitor();
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setLoaded(true);
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const handleImageLoad = t5;
  let t6;
  if ($[1] !== errorCount || $[2] !== maxRetries) {
    t6 = () => {
      if (errorCount >= maxRetries) {
        return;
      }
      const delay = 500 * 2 ** errorCount;
      setTimeout(() => {
        setErrorCount(_temp);
        setRetryTick(_temp2);
      }, delay);
    };
    $[1] = errorCount;
    $[2] = maxRetries;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const handleError = t6;
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setErrorCount(0);
      setLoaded(false);
      setRetryTick(_temp3);
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const manualRetry = t7;
  let t8;
  if ($[5] !== disableModal) {
    t8 = event => {
      if (disableModal) {
        return;
      }
      event.stopPropagation();
      setIsModalOpen(true);
    };
    $[5] = disableModal;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const handleImageClick = t8;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = event_0 => {
      event_0?.stopPropagation();
      setIsModalOpen(false);
    };
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const handleCloseModal = t9;
  let t10;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = event_1 => {
      event_1.stopPropagation();
      const fullscreenTarget = modalImageRef.current ?? imgRef.current;
      if (fullscreenTarget) {
        fullscreenTarget.requestFullscreen();
      }
    };
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const handleFullScreen = t10;
  let t11;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      width: "100%",
      height: "100%",
      maxWidth: "100%",
      maxHeight: "100%",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    };
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const loadingPlaceholderStyle = t11;
  let t12;
  if ($[10] !== disableModal) {
    t12 = () => {
      if (!disableModal) {
        handleCloseModal();
      }
    };
    $[10] = disableModal;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  useKeyPressEvent("Escape", t12);
  let t13;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="tw-pointer-events-none tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-80" />;
    $[12] = t13;
  } else {
    t13 = $[12];
  }
  let t14;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      disabled: true
    };
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = !isZoomed;
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = e_2 => setIsZoomed(e_2.state.scale > 1);
    $[14] = t16;
  } else {
    t16 = $[14];
  }
  let t17;
  if ($[15] !== isCapacitor || $[16] !== isZoomed || $[17] !== onContainerClick || $[18] !== src) {
    t17 = t18 => {
      const {
        resetTransform
      } = t18;
      return <div className="tw-fixed tw-inset-0 tw-z-1000 tw-flex tw-items-center tw-justify-center tw-overflow-hidden"><div className="tw-relative tw-flex tw-max-h-[90vh] tw-max-w-[95vw] tw-flex-col lg:tw-flex-row"><div role="button" className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center" onClick={_temp7} tabIndex={0} aria-label="Full size drop media" onKeyDown={_temp8}><TransformComponent wrapperClass="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center" contentClass="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><img ref={modalImageRef} src={src} alt="Full size drop media" style={{
                pointerEvents: "auto"
              }} className="tw-max-h-[75vh] tw-max-w-full tw-object-contain lg:tw-max-h-[90vh]" /></TransformComponent></div><div className="tw-fixed tw-right-4 tw-top-2 tw-z-[1001] tw-flex tw-flex-row tw-gap-x-4 tw-pt-[env(safe-area-inset-top,0px)] lg:tw-relative lg:tw-right-auto lg:tw-top-0 lg:tw-ml-4 lg:tw-flex-col-reverse lg:tw-gap-x-0 lg:tw-gap-y-2 lg:tw-self-start lg:tw-pt-0">{isZoomed && <button onClick={e_5 => {
              e_5.stopPropagation();
              resetTransform();
              setIsZoomed(false);
            }} data-tooltip-id="reset-zoom" className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-800 tw-text-iron-50 tw-backdrop-blur-sm tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-700" aria-label="Reset"><FontAwesomeIcon icon={faRotateLeft} className="tw-size-4" /></button>}<Link href={src} target="_blank" rel="noopener noreferrer"><button onClick={_temp9} data-tooltip-id={`open-browser-${src}`} className={modalButtonClasses} aria-label="Open image in new tab"><ArrowTopRightOnSquareIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /></button></Link>{fullScreenSupported() && !isCapacitor && <button onClick={handleFullScreen} data-tooltip-id="full-screen" className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-800 tw-text-iron-50 tw-backdrop-blur-sm tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-700" aria-label="Full screen"><FontAwesomeIcon icon={faExpand} className="tw-size-4" /></button>}{onContainerClick && <button onClick={e_7 => {
              e_7.stopPropagation();
              handleCloseModal();
              onContainerClick();
            }} data-tooltip-id="view-drop-details" className="tw-flex tw-size-10 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-iron-800 tw-text-iron-50 tw-backdrop-blur-sm tw-transition-all tw-duration-300 tw-ease-out desktop-hover:hover:tw-bg-iron-700" aria-label="View drop details"><InformationCircleIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /></button>}<button onClick={handleCloseModal} data-tooltip-id="close-modal" className={modalButtonClasses} aria-label="Close modal"><svg className="tw-h-5 tw-w-5 tw-flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button></div></div></div>;
    };
    $[15] = isCapacitor;
    $[16] = isZoomed;
    $[17] = onContainerClick;
    $[18] = src;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== t15 || $[21] !== t17) {
    t18 = <TransformWrapper panning={t14} limitToBounds={t15} smooth={true} onZoom={t16}>{t17}</TransformWrapper>;
    $[20] = t15;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== isCapacitor || $[24] !== src) {
    t19 = !isCapacitor && <><Tooltip id={`open-browser-${src}`} {...tooltipProps}><span className="tw-text-xs">Open in Browser</span></Tooltip><Tooltip id="full-screen" {...tooltipProps}><span className="tw-text-xs">Full screen</span></Tooltip><Tooltip id="view-drop-details" {...tooltipProps}><span className="tw-text-xs">View Drop details</span></Tooltip><Tooltip id="reset-zoom" {...tooltipProps}><span className="tw-text-xs">Reset zoom</span></Tooltip><Tooltip id="close-modal" {...tooltipProps}><span className="tw-text-xs">Close</span></Tooltip></>;
    $[23] = isCapacitor;
    $[24] = src;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t18 || $[27] !== t19) {
    t20 = <div className="tailwind-scope tw-relative tw-z-1000 tw-cursor-default" onClick={handleCloseModal} onTouchStart={_temp4} onTouchEnd={_temp5} onTouchMove={_temp6}>{t13}{t18}{t19}</div>;
    $[26] = t18;
    $[27] = t19;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const modalContent = t20;
  const resolvedObjectPosition = imageObjectPosition ?? (isCompetitionDrop ? "center" : "left top");
  const t21 = `tw-relative tw-flex tw-h-full tw-w-full tw-items-center ${isCompetitionDrop ? "tw-justify-center" : ""}`;
  let t22;
  if ($[29] !== errorCount || $[30] !== hasTouchScreen || $[31] !== loaded || $[32] !== maxRetries) {
    t22 = !loaded && errorCount <= maxRetries && <div className={`tw-rounded-xl tw-bg-iron-800 ${hasTouchScreen ? "" : "tw-animate-pulse"}`} style={loadingPlaceholderStyle} />;
    $[29] = errorCount;
    $[30] = hasTouchScreen;
    $[31] = loaded;
    $[32] = maxRetries;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== disableModal || $[35] !== errorCount || $[36] !== handleError || $[37] !== handleImageClick || $[38] !== imageScale || $[39] !== inView || $[40] !== loaded || $[41] !== maxRetries || $[42] !== resolvedObjectPosition || $[43] !== retryTick || $[44] !== src) {
    t23 = inView && errorCount <= maxRetries && <FallbackImage key={retryTick} ref={imgRef} primarySrc={getScaledImageUri(src, imageScale)} fallbackSrc={src} alt="Drop media" fill={true} sizes="(max-width: 768px) 100vw, 768px" className={`tw-max-h-full tw-max-w-full ${!loaded ? "tw-opacity-0" : "tw-opacity-100"} ${disableModal ? "" : "tw-cursor-pointer"}`} style={{
      objectFit: "contain",
      objectPosition: resolvedObjectPosition
    }} onLoad={handleImageLoad} onClick={handleImageClick} onError={handleError} />;
    $[34] = disableModal;
    $[35] = errorCount;
    $[36] = handleError;
    $[37] = handleImageClick;
    $[38] = imageScale;
    $[39] = inView;
    $[40] = loaded;
    $[41] = maxRetries;
    $[42] = resolvedObjectPosition;
    $[43] = retryTick;
    $[44] = src;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== errorCount || $[47] !== maxRetries) {
    t24 = errorCount > maxRetries && <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2"><span className="tw-text-sm tw-text-iron-400">Couldn’t load image.</span><button onClick={manualRetry} className="tw-rounded-md tw-bg-iron-700 tw-px-3 tw-py-1 tw-text-xs tw-text-white hover:tw-bg-iron-600">Retry</button></div>;
    $[46] = errorCount;
    $[47] = maxRetries;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== ref || $[50] !== t21 || $[51] !== t22 || $[52] !== t23 || $[53] !== t24) {
    t25 = <div ref={ref} className={t21}>{t22}{t23}{t24}</div>;
    $[49] = ref;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== disableModal || $[56] !== isModalOpen || $[57] !== modalContent) {
    t26 = !disableModal && isModalOpen && createPortal(modalContent, document.body);
    $[55] = disableModal;
    $[56] = isModalOpen;
    $[57] = modalContent;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t25 || $[60] !== t26) {
    t27 = <>{t25}{t26}</>;
    $[59] = t25;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  return t27;
}
function _temp9(e_6) {
  return e_6.stopPropagation();
}
function _temp8(e_4) {
  if (e_4.key === "Enter" || e_4.key === " ") {
    e_4.stopPropagation();
  }
}
function _temp7(e_3) {
  return e_3.stopPropagation();
}
function _temp6(e_1) {
  return e_1.stopPropagation();
}
function _temp5(e_0) {
  return e_0.stopPropagation();
}
function _temp4(e) {
  return e.stopPropagation();
}
function _temp3(t_0) {
  return t_0 + 1;
}
function _temp2(t) {
  return t + 1;
}
function _temp(n) {
  return n + 1;
}
export default React.memo(DropListItemContentMediaImage);